import { Box, Drawer, Typography, useMediaQuery, useTheme } from "@mui/material";
import { NavLink, useNavigate } from "react-router-dom";
import { useConstants } from "../../hooks/UseConstants";
import { useSidebarDrawer } from "../../context/SidebarDrawerContext";
import LogoImage from "../../images/logo/logo.png";
import { APP_NAME } from "../../constants/brand";

function AppSidebarShell({ sections, homeLink = "/dashboard", show = true }) {
    const { language } = useConstants();
    const theme = useTheme();
    const navigate = useNavigate();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });
    const { open: drawerOpen, closeDrawer } = useSidebarDrawer();
    const isDark = theme.palette.mode === "dark";
    const isEn = language === "en";

    if (!show) return null;

    const sidebarSx = {
        ...(isEn ? { left: 0, right: "auto" } : { right: 0, left: "auto" }),
        ...(isDark
            ? {
                  background: `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
                  borderInlineEnd: `1px solid ${theme.palette.divider}`,
              }
            : {}),
    };

    const afterNavigate = () => {
        if (!isDesktop) closeDrawer();
    };

    const renderItems = () =>
        sections.map((section, sIdx) => (
            <Box key={sIdx} className="app-sidebar__group">
                {section.label ? (
                    <Typography component="p" className="app-sidebar__section-label">
                        {section.label}
                    </Typography>
                ) : null}
                {section.items.map((item) => {
                    const Icon = item.icon;
                    return (
                        <NavLink
                            key={item.link}
                            to={item.link}
                            end={item.end}
                            title={typeof item.title === "string" ? item.title : undefined}
                            className={({ isActive }) =>
                                `app-sidebar__link${isActive ? " app-sidebar__link--active" : ""}`
                            }
                            onClick={afterNavigate}
                        >
                            <span className="app-sidebar__icon">
                                <Icon fontSize="small" />
                            </span>
                            <Typography component="span" className="app-sidebar__label">
                                {item.title}
                            </Typography>
                        </NavLink>
                    );
                })}
            </Box>
        ));

    const sidebarPanel = (className) => (
        <Box component="aside" className={className} dir={isEn ? "ltr" : "rtl"} sx={sidebarSx}>
            <Box className="app-sidebar__brand" sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box
                    component="span"
                    className="app-sidebar__logo-wrap"
                    onClick={() => {
                        afterNavigate();
                        navigate(homeLink);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            afterNavigate();
                            navigate(homeLink);
                        }
                    }}
                    sx={{ display: "inline-flex", width: "fit-content" }}
                >
                    <img src={LogoImage} alt={APP_NAME} className="app-sidebar__logo" />
                </Box>
                <Typography className="app-sidebar__tagline" variant="caption">
                    {isEn ? "E-Learning Platform" : "منصة التعلم الإلكتروني"}
                </Typography>
            </Box>

            <Box component="nav" className="app-sidebar__nav none-view-scroll">
                {renderItems()}
            </Box>
        </Box>
    );

    return (
        <>
            {isDesktop ? sidebarPanel("app-sidebar app-sidebar--desktop") : null}
            {!isDesktop ? (
                <Drawer
                    anchor={isEn ? "left" : "right"}
                    open={drawerOpen}
                    onClose={closeDrawer}
                    PaperProps={{
                        sx: {
                            width: "min(88vw, 300px)",
                            bgcolor: "transparent",
                            border: "none",
                            boxShadow: "none",
                        },
                    }}
                    ModalProps={{ keepMounted: true }}
                >
                    {sidebarPanel("app-sidebar app-sidebar--drawer")}
                </Drawer>
            ) : null}
        </>
    );
}

export default AppSidebarShell;
